import { isIEditorV3 } from "@azu-business/editor-v3";
import { classMerge, isArsListingBody, isArsTableBody, useArsContext } from "aic";
import { useHierarchyContext, useTlfContext } from "contexts/AppSettingsContext";
import { eAccessRights } from "functions/api/enums";
import { ReactNode, useState } from "react";
import { PencilSquare, PlusCircle } from "react-bootstrap-icons";
import styles from "../annotations/AnnotationButton.module.css";
import { ArsSetWindow } from "./ArsSetWindow";
import { ARS_SET_TYPES, ArsSetType } from "./interface";

/**
 * Props for ArsSetPicker.
 */
export interface ArsSetPickerProps extends Omit<
  React.HTMLAttributes<HTMLButtonElement>,
  "children" | "id" | "title" | "aria-label" | "onChange" | "onClick" | "type"
> {
  /* Callback for when the selection changes, used to trigger side effects in parent components. */
  onChange: (newItemId: string | undefined) => void;
  /** DOM id for the trigger button. */
  itemId: string;
  /** Which ARS set type this picker controls. */
  type: ArsSetType;
}

/**
 * Shared picker button for ARS set selection/editing windows.
 *
 * @param props - Component props.
 * @param props.onChange - Callback for when the selection changes, used to trigger side effects in parent components.
 * @param props.itemId - DOM id for the trigger button.
 * @param props.type - Which ARS set type this picker controls.
 * @returns Picker trigger button and contextual window.
 */
export const ArsSetPicker = ({ type, itemId, onChange, ...rest }: ArsSetPickerProps): ReactNode => {
  const hierarchyContext = useHierarchyContext();
  const tlfContext = useTlfContext();
  const ars = useArsContext();
  const [showWindow, setShowWindow] = useState(false);

  const canEdit =
    hierarchyContext?.accessRights.includes(eAccessRights.WriteToTlfItemsInHierarchy) ?? false;
  const body =
    isArsListingBody(tlfContext.tlfData?.body, isIEditorV3) ||
    isArsTableBody(tlfContext.tlfData?.body, isIEditorV3)
      ? tlfContext.tlfData.body
      : null;

  const hasSelection =
    type === "analysisMethod"
      ? ars.state.analysisMethods[itemId] !== undefined
      : type === "analysisSet"
        ? ars.state.analysisSets[itemId] !== undefined
        : type === "dataSubset"
          ? ars.state.dataSubsets[itemId] !== undefined
          : ars.state.groupingFactors[itemId] !== undefined;

  const selectLabel = ARS_SET_TYPES[type].selectLabel;
  const glyphText = ARS_SET_TYPES[type].glyphText;

  return !hierarchyContext?.hierarchy || !body ? null : (
    <>
      <button
        {...rest}
        id={itemId}
        title={selectLabel}
        type="button"
        aria-label={selectLabel}
        onClick={() => setShowWindow((prev) => !prev)}
        className={classMerge([
          styles.annotationBtn,
          !hasSelection && ["analysisMethod", "analysisSet"].includes(type)
            ? styles.badEmpty
            : !hasSelection
              ? styles.empty
              : undefined,
          "position-relative",
        ])}
      >
        <span
          className={styles.glyph}
          aria-hidden="true"
        >
          <div className={classMerge([styles.icon, styles.wide])}>
            <span className={styles.letter}>{glyphText}</span>
            {canEdit && (
              <span className={styles.hoverIcon}>
                {hasSelection ? <PencilSquare size={12} /> : <PlusCircle size={12} />}
              </span>
            )}
          </div>
        </span>
      </button>

      <ArsSetWindow
        itemId={itemId}
        type={type}
        visible={showWindow}
        onClose={(newItemId) => {
          setShowWindow(false);
          // Undefined returned on cancel.
          // Empty string returned on delete.
          if (newItemId !== undefined) {
            onChange(newItemId !== "" ? newItemId : undefined);
          }
        }}
      />
    </>
  );
};

ArsSetPicker.displayName = "ArsSetPicker";
